import { fetchNoAuth } from "../utils/fetch/FetchNoAuth";
import { fetchWithAuth } from "../utils/fetch/FetchWithAuth";
import { getRuntimeConfig } from "../utils/runtimeConfig";
import { prepareBaseGetListParams } from "../utils/fetch/PrepareBaseGetListParams";
import { prepareEnumSearchParams } from "../utils/fetch/PrepareEnumSearchParams";
import { pickFields } from "../utils/pickFields";


export const UserService = {

    getList: (criteria, enums) => {
        console.log('UserService::getList-criteria', criteria);
        const params = prepareBaseGetListParams(criteria);
        prepareEnumSearchParams(criteria.baseQuery, params, enums);
        params.append('only_active', criteria.showOnlyActive || false);
        if (criteria.userRoleId) {
            params.append('role_id', criteria.userRoleId);
        }
        const url = `/portal/users?${params.toString()}`;
        return fetchWithAuth(url, { method: 'GET' });

    },
    getOwnProfile: () => {
        return fetchWithAuth('/portal/users/own-profile', { method: 'GET' });
    },

    getStatusList: () => {
        return fetchWithAuth('/portal/users/status-list', { method: 'GET' });
    },

    uploadAvatar: (userId, fileBlob) => {
        const formData = new FormData();
        formData.append('file', fileBlob, 'avatar.jpg');
        return fetchWithAuth(`/portal/users/avatar${userId ? '?userId=' + userId : ''}`, { method: 'POST', body: formData }, true);
    },

    create: (user) => {
        const u = pickFields(user, {
            userName: true,
            role: { id: true },
            email: true,
            phone: true,
            validUntilDate: true,
            avatarId: true,
            operator: { id: true },
            merchant: { id: true },
            allDevicesAssigned: true,
            allLocationsAssigned: true,
        });
        u.status = user.status?.id;
        // Dodaj przypisania jeśli są dostępne
        if (user.assignedLocations && Array.isArray(user.assignedLocations) && user.assignedLocations.length > 0) {
            u.assignedLocations = user.assignedLocations;
        }
        if (user.assignedDevices && Array.isArray(user.assignedDevices) && user.assignedDevices.length > 0) {
            u.assignedDevices = user.assignedDevices;
        }
        return fetchWithAuth('/portal/users', { method: 'POST', body: JSON.stringify(u) });
    },

    update: (user) => {
        const u = pickFields(user, {
            id: true,
            userName: true,
            role: { id: true },
            email: true,
            phone: true,
            validUntilDate: true,
            avatarId: true,
            operator: { id: true },
            merchant: { id: true }
        });
        u.status = user.status?.id;
        return fetchWithAuth(`/portal/users/${user.id}`, { method: 'PUT', body: JSON.stringify(u) });
    },

    getEmailStatus: (email, userId, roleId) => {
        return fetchWithAuth('/portal/users/email-status', { method: 'PUT', body: JSON.stringify({ email, userId, roleId }) });
    },

    getPhoneStatus: (phone, userId, roleId) => {
        return fetchWithAuth('/portal/users/phone-status', { method: 'PUT', body: JSON.stringify({ phone, userId, roleId }) });
    },

    getAvatarUrl: (user) => {
        const domain = getRuntimeConfig('REACT_APP_BACKEND_URL');
        return (user.avatarExists || user.avatarId) ? `${domain}/portal/users/avatar?userId=${user.id}` : null;
    },

    get: (uuid) => {
        return fetchWithAuth(`/portal/users/${uuid}`, { method: 'GET' });
    },

    validateUserToken: (token) => {
        return fetchNoAuth('/portal/users/validate-user-token', { method: 'PUT', body: JSON.stringify({ token }) });
    },

    fetchPasswordPolicy: () => {
        return fetchNoAuth('/portal/users/password-policy', { method: 'GET' });
    },

    setPassword: (token, password) => {
        return fetchNoAuth('/portal/users/set-user-password', { method: 'PUT', body: JSON.stringify({ token, password }) });
    },

    passwordRecovery: (email) => {
        return fetchNoAuth('/portal/users/password-recovery', { method: 'PUT', body: JSON.stringify({ email }) });
    },

    deactivate: (userId) => {
        return fetchWithAuth(`/portal/users/${userId}/deactivate`, { method: 'PUT' });
    },

    delete: (userId) => {
        return fetchWithAuth(`/portal/users/${userId}`, { method: 'DELETE' });
    },

    activate: (userId) => {
        return fetchWithAuth(`/portal/users/${userId}/activate`, { method: 'PUT' });
    },

    getTechniciansList: () => {
        return fetchWithAuth('/portal/users/technicians-list', { method: 'GET' });
    },

    getAssignedLocations: (userId, criteria, { signal } = {}) => {
        const params = prepareBaseGetListParams(criteria);
        // Sprawdź, czy backend wymaga innych parametrów sortowania
        // Jeśli backend używa Spring Pageable, może wymagać parametru 'sort' zamiast 'sort_field' i 'sort_asc'
        // Jeśli backend używa LocationCriteria, może wymagać dodania sortField i sortAsc do kryteriów
        const url = `/portal/users/${userId}/assigned-locations?${params.toString()}`;
        console.log('UserService::getAssignedLocations - URL:', url);
        console.log('UserService::getAssignedLocations - criteria:', criteria);
        return fetchWithAuth(url, { method: 'GET', signal });
    },

    assignLocations: (userId, locationIds) => {
        return fetchWithAuth(`/portal/users/${userId}/assigned-locations`, { 
            method: 'PUT', 
            body: JSON.stringify({ locationIds }) 
        });
    },

    unassignLocationsList: (userId, criteria, selectedAll, exceptionIds) => {
        const params = prepareBaseGetListParams(criteria, false);
        return fetchWithAuth(`/portal/users/${userId}/assigned-locations/unassign-list?${params.toString()}`, { 
            method: 'DELETE',
            body: JSON.stringify({ selectedAll, exceptions: exceptionIds })
        });
    },

    getAssignedDevices: (userId, criteria) => {
        const params = prepareBaseGetListParams(criteria);
        const url = `/portal/users/${userId}/assigned-devices?${params.toString()}`;
        return fetchWithAuth(url, { method: 'GET' });
    },

    assignDevices: (userId, deviceIds) => {
        return fetchWithAuth(`/portal/users/${userId}/assigned-devices`, { 
            method: 'PUT', 
            body: JSON.stringify({ deviceIds }) 
        });
    },

    unassignDevicesList: (userId, criteria, selectedAll, exceptionIds) => {
        const params = prepareBaseGetListParams(criteria, false);
        return fetchWithAuth(`/portal/users/${userId}/assigned-devices/unassign-list?${params.toString()}`, { 
            method: 'DELETE',
            body: JSON.stringify({ selectedAll, exceptions: exceptionIds })
        });
    },

    setAllDevicesAssigned: (userId, value) => {
        return fetchWithAuth(`/portal/users/${userId}/all-devices-assigned`, { 
            method: 'PUT',
            body: JSON.stringify({ value })
        });
    },

    setAllLocationsAssigned: (userId, value) => {
        return fetchWithAuth(`/portal/users/${userId}/all-locations-assigned`, { 
            method: 'PUT',
            body: JSON.stringify({ value })
        });
    },

    getAllDevicesAssigned: (userId) => {
        return fetchWithAuth(`/portal/users/${userId}/all-devices-assigned`, { method: 'GET' });
    },

    getAllLocationsAssigned: (userId) => {
        return fetchWithAuth(`/portal/users/${userId}/all-locations-assigned`, { method: 'GET' });
    },

    searchDevicesForUser: (criteria, deviceIds) => {
        const params = prepareBaseGetListParams(criteria);
        const url = `/portal/users/search-devices?${params.toString()}`;
        return fetchWithAuth(url, {
            method: 'POST',
            body: JSON.stringify({ deviceIds: deviceIds || [] })
        });
    },

    searchLocationsForUser: (criteria, locationIds) => {
        const params = prepareBaseGetListParams(criteria);
        const url = `/portal/users/search-locations?${params.toString()}`;
        return fetchWithAuth(url, {
            method: 'POST',
            body: JSON.stringify({ locationIds: locationIds || [] })
        });
    },

    // Get only IDs of filtered devices after filtering (for select all functionality)
    searchDevicesForUserIds: (criteria, deviceIds) => {
        const params = prepareBaseGetListParams(criteria);
        const url = `/portal/users/search-devices-ids?${params.toString()}`;
        return fetchWithAuth(url, {
            method: 'POST',
            body: JSON.stringify({ deviceIds: deviceIds || [] })
        });
    },

    // Get only IDs of filtered locations after filtering (for select all functionality)
    searchLocationsForUserIds: (criteria, locationIds) => {
        const params = prepareBaseGetListParams(criteria);
        const url = `/portal/users/search-locations-ids?${params.toString()}`;
        return fetchWithAuth(url, {
            method: 'POST',
            body: JSON.stringify({ locationIds: locationIds || [] })
        });
    },

    createSSOToken: (teamId) => {
        return fetchWithAuth('/portal/users/guidejar/sso-token', {
            method: 'POST',
            body: JSON.stringify({ teamId })
        });
    }
}